<script setup lang="ts">
import { api } from '@/api';
import { RequestSerialNumberAssignmentSupportForDeviceRequest } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import { success as successIcon } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { isIOTDevice } from '@/utils/rtSystem';
import { I18nKey } from '@/utils/typings.ts';
import {
  isRegistrablePtwQaEquipment,
  getTypeLabel,
  getModelLabel,
} from '@/views/qaEquipment/utils';

import { ref } from 'vue';

const modelValue = defineModel<boolean>({ type: Boolean, default: false });

const qaEquipmentStore = useQaEquipmentStore();

const emailSentSuccessfully = ref(false);

const { form } = useVeeForm<RequestSerialNumberAssignmentSupportForDeviceRequest>({
  mode: 'edit',
  options: {
    initialValues: {
      id: qaEquipmentStore.deviceForRegistration?.id,
      supportRequestDto: {
        $type:
          'Ptw.Ptw100.AppZero.Application.Shared.DeviceComponent.Dto.SupportRequestDto, Ptw.Ptw100.AppZero.Application.Shared',
      },
    },
  },
});

const { handleSubmit, resetForm } = form;

const submit = handleSubmit(async (requestSerialNumberAssignmentSupportForDeviceRequest) => {
  const { success } = await api.qaEquipmentCloudApi.requestSerialNumberAssignmentSupportForDevice(
    requestSerialNumberAssignmentSupportForDeviceRequest,
    [formBackendErrorHandler(form)],
  );
  if (success) {
    emailSentSuccessfully.value = true;
  }
});

function finaliseAssignmentRequest() {
  modelValue.value = false;
  resetForm();
  emailSentSuccessfully.value = false;
}

function cancel() {
  modelValue.value = false;
}
</script>

<template>
  <Dialog
    v-if="
      qaEquipmentStore.deviceForRegistration &&
      isRegistrablePtwQaEquipment(qaEquipmentStore.deviceForRegistration)
    "
    v-model="modelValue"
    aria-label="RequestAssignmentDialog"
    hideClose
    maxWidth="480"
  >
    <template #title>{{ $t('RequestAssignmentOfSerialNumber') }}</template>
    <template #text>
      <FormLabel :label="`${$t('Name')} | ${$t('ID')}`">
        <IdentifierLabel
          :label="qaEquipmentStore.deviceForRegistration.name"
          :identifier="qaEquipmentStore.deviceForRegistration.iuid"
        />
      </FormLabel>
      <div class="d-flex flex-wrap justify-start mt-4">
        <TextField
          class="w-50"
          :label="$t('Type_one')"
          :modelValue="$t(getTypeLabel(qaEquipmentStore.deviceForRegistration) as I18nKey)"
          mode="details"
        />
        <TextField
          class="w-50"
          :label="$t('Model')"
          :modelValue="getModelLabel(qaEquipmentStore.deviceForRegistration)"
          mode="details"
        />
        <TextField
          class="w-50 gutterTop"
          :label="$t('ArticleNumber')"
          :modelValue="qaEquipmentStore.deviceForRegistration.typeNumber || undefined"
          mode="details"
        />
        <TextField
          class="w-50 gutterTop"
          :label="$t('SerialNumber')"
          :modelValue="qaEquipmentStore.deviceForRegistration.serialNumber"
          mode="details"
        />
        <TextField
          v-if="
            isIOTDevice(qaEquipmentStore.deviceForRegistration) &&
            'dpin' in qaEquipmentStore.deviceForRegistration
          "
          class="gutterTop"
          :label="$t('DPIN')"
          :modelValue="qaEquipmentStore.deviceForRegistration.dpin"
          mode="details"
        />
      </div>

      <VeeTextarea
        class="gutterTop"
        :placeholder="$t('AssignmentRequestMessagePlaceholder')"
        :label="$t('Message')"
        rows="8"
        name="supportRequestDto.message"
        :mode="emailSentSuccessfully ? 'details' : 'edit'"
      />

      <v-alert
        v-if="emailSentSuccessfully"
        class="text-body-1 font-weight-bold mt-8 mb-4"
        type="success"
        :icon="successIcon"
        :text="$t('MessageSentWeWillGetInTouchMsg')"
        density="default"
        variant="tonal"
      />
    </template>

    <template #actions>
      <template v-if="emailSentSuccessfully">
        <v-spacer />

        <SecondaryVBtn
          :text="$t('Close')"
          class="align-self-center"
          @click="finaliseAssignmentRequest"
        />

        <v-spacer />
      </template>

      <template v-else>
        <TextVBtn @click="cancel">
          {{ $t('Cancel') }}
        </TextVBtn>

        <VeeSubmitButton class="ml-4" :text="$t('Send')" @click="submit" />
      </template>
    </template>
  </Dialog>
</template>
